import { RecoSectionContent } from '@aboutyou/legacy-tracking-sdk'
import { exists } from 'helpers/exists'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { cdnImageUrl } from 'helpers/image'
import {
  DeferredSectionsTrackingContextProvider,
  RecoGridSectionTrackingContextProvider,
  SectionTrackingContextProvider,
} from 'helpers/tracking/SectionTrackingContext'
import React, { FC, memo, useRef } from 'react'
import { useService } from 'services/utils/context'
import ProductTileConnected from 'sharedComponents/ProductTileConnected/ProductTileConnected'
import Outfit from 'store/entities/Outfit/Outfit.store'
import DividerSection from 'uiComponents/atoms/Sections/DividerSection'
import OutfitReco from 'uiComponents/organisms/OutfitReco/OutfitReco'

const OutfitTrackingSection: FC<
  React.PropsWithChildren<{ headline?: string }>
> = memo(function OutfitTrackingSection({ children, headline }) {
  return (
    <RecoGridSectionTrackingContextProvider
      headline={headline}
      recoType={RecoSectionContent.outfitRecommendationProducts}
    >
      {children}
    </RecoGridSectionTrackingContextProvider>
  )
})

const OutfitTrackingSectionMainOutfit: FC<React.PropsWithChildren> = memo(
  function OutfitTrackingSectionMainOutfit({ children }) {
    return (
      <SectionTrackingContextProvider>
        {children}
      </SectionTrackingContextProvider>
    )
  },
)

interface Props {
  outfit: Outfit | null
  shifted?: boolean
  mainOutfit?: boolean
}

const OdpOutfitReco: FC<React.PropsWithChildren<Props>> = ({
  outfit,
  shifted,
  mainOutfit,
}) => {
  const EndWrapper = mainOutfit
    ? OutfitTrackingSectionMainOutfit
    : OutfitTrackingSection

  const outfitRecoRef = useRef<HTMLDivElement>(null)
  const service = useService()
  const { t } = useTranslation()

  if (!outfit) {
    return null
  }

  const outfitProducts = outfit.productIds
    .filter(id => exists(service.products.getById(id)))
    .map(id => (
      <ProductTileConnected
        key={id}
        productId={id}
        preferredImageType="bust"
        enableAlternativesButton
      />
    ))

  const subTitle = outfit.idol
    ? t('Outfit von {{-name}}', { name: outfit.idol?.name })
    : null

  return (
    <DividerSection ref={outfitRecoRef}>
      <OutfitReco
        ProductGridTrackingWrapper={EndWrapper}
        shifted={shifted}
        subTitle={subTitle}
        outfitProducts={outfitProducts}
        stickyRef={outfitRecoRef}
        sliderImgSources={outfit.portraitImages.map(hash =>
          cdnImageUrl(hash, { width: 800, height: 800 }),
        )}
      />
    </DividerSection>
  )
}

const TrackedOdpOutfitReco: FC<Props> = props => (
  <DeferredSectionsTrackingContextProvider sectionCount={1}>
    <OdpOutfitReco {...props} />
  </DeferredSectionsTrackingContextProvider>
)

export default TrackedOdpOutfitReco
